// „Склад по продукт" в „Конфигурации": продуктите със СВОЙ склад в ресторанта —
// при разнасяне на отчет и при брак от сторно те се изписват оттам, вместо от
// склада на станцията на рецептата (правилото е на BE, по съставка). Списък,
// смяна на склада, махане и добавяне. Невалидна настройка (складът вече не е
// на ресторанта) се показва с червено и BE я пренебрегва — не се трие сама.
// Всяка промяна се записва веднага (PUT/DELETE), както другите настройки тук.
import { useMemo, useState } from "react";
import { App, Select, Spin } from "antd";
import { CloseOutlined, PushpinOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useProducts, useWarehouses } from "../../../hooks/useInventory";
import {
  useDeleteProductWarehouse,
  useProductWarehouses,
  useSetProductWarehouse,
} from "../../../hooks/useInventoryProductWarehouses";
import { SectionCard, StatusPill } from "../components/list/ListBits";
import { ROW_DENSE, ROW_ICON_BUTTON } from "../components/list/density";
import { SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { pickableProducts } from "../components/product/deletedProducts";

type Props = { isNarrow?: boolean };

export const ProductWarehousesSection = ({ isNarrow = false }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data: rows = [], isLoading } = useProductWarehouses();
  const { data: products = [] } = useProducts();
  const { data: warehouses = [] } = useWarehouses();
  const setMutation = useSetProductWarehouse();
  const deleteMutation = useDeleteProductWarehouse();
  const busy = setMutation.isPending || deleteMutation.isPending;

  const [newProductId, setNewProductId] = useState<number | null>(null);
  const [newWarehouseId, setNewWarehouseId] = useState<number | null>(null);

  const warehouseOptions = warehouses.map((warehouse) => ({
    value: warehouse.id,
    label: warehouse.name,
  }));
  // Продуктите без свой склад — само тях може да добави; изтритите не се избират
  const productOptions = useMemo(() => {
    const taken = new Set(rows.map((row) => row.productId));
    return pickableProducts(products)
      .filter((product) => !taken.has(product.id))
      .map((product) => ({ value: product.id, label: product.name }));
  }, [products, rows]);

  const onError = (error: unknown) => {
    const err = error as { response?: { data?: { detail?: string } } };
    message.error(err.response?.data?.detail || t("inventoryPage.errSave"));
  };
  const save = (productId: number, warehouseId: number, onDone?: () => void) =>
    setMutation.mutate(
      { productId, warehouseId },
      {
        onSuccess: () => {
          message.success(t("inventoryPage.productWarehouseSaved"));
          onDone?.();
        },
        onError,
      },
    );
  const remove = (productId: number) =>
    deleteMutation.mutate(productId, {
      onSuccess: () => message.success(t("inventoryPage.productWarehouseRemoved")),
      onError,
    });
  const add = () => {
    if (newProductId == null || newWarehouseId == null) return;
    save(newProductId, newWarehouseId, () => {
      setNewProductId(null);
      setNewWarehouseId(null);
    });
  };

  return (
    <SectionCard className="flex flex-col gap-4 px-[22px] py-5">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-brand-deep-ink">
          <PushpinOutlined className="text-lg" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[15px] font-semibold leading-[1.2] text-ink">
            {t("inventoryPage.productWarehousesSection")}
          </span>
          <span className="text-[12.5px] leading-[1.4] text-muted">
            {t("inventoryPage.productWarehousesHint")}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <span className="text-[13px] text-muted">{t("inventoryPage.productWarehousesEmpty")}</span>
      ) : (
        <div className="flex flex-col">
          {rows.map((row) => (
            <div
              key={row.productId}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-shade-1 first:border-t-0 ${ROW_DENSE}`}
            >
              <span className="min-w-0 flex-1 truncate font-medium text-ink">{row.productName}</span>
              {!row.valid && (
                <StatusPill tone="danger">{t("inventoryPage.productWarehouseInvalid")}</StatusPill>
              )}
              <Select<number>
                size="small"
                value={row.valid ? row.warehouseId : undefined}
                placeholder={t("inventoryPage.fieldWarehouse")}
                onChange={(warehouseId) => save(row.productId, warehouseId)}
                disabled={busy}
                options={warehouseOptions}
                className={isNarrow ? "min-w-[140px] flex-1" : "w-[200px]"}
              />
              <button
                type="button"
                aria-label={t("inventoryPage.productWarehouseRemove", { product: row.productName })}
                title={t("inventoryPage.productWarehouseRemove", { product: row.productName })}
                onClick={() => remove(row.productId)}
                disabled={busy}
                className={`${ROW_ICON_BUTTON} inline-flex items-center justify-center rounded-md text-muted hover:bg-danger-tint hover:text-danger-ink disabled:opacity-50`}
              >
                <CloseOutlined />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[13px] font-semibold text-body">
          {t("inventoryPage.productWarehouseAddQuestion")}
        </span>
        <Select<number>
          showSearch
          optionFilterProp="label"
          value={newProductId ?? undefined}
          onChange={setNewProductId}
          placeholder={t("inventoryPage.productWarehouseProductPh")}
          options={productOptions}
          className={isNarrow ? "w-full" : "min-w-[220px]"}
        />
        <Select<number>
          value={newWarehouseId ?? undefined}
          onChange={setNewWarehouseId}
          placeholder={t("inventoryPage.fieldWarehouse")}
          options={warehouseOptions}
          className={isNarrow ? "w-full" : "min-w-[160px]"}
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || newProductId == null || newWarehouseId == null}
          className={SECONDARY_BUTTON_CLASS}
        >
          {t("inventoryPage.productWarehouseAdd")}
        </button>
      </div>
    </SectionCard>
  );
};

export default ProductWarehousesSection;
